import * as Sentry from "@sentry/vue";
import dayjs from "dayjs";

/*
    สถานะการ sync ข้อมูลให้มือถือของเครื่องนี้ (tables / live / tableMeta ทั้ง RTDB และ Firestore)

    ทำไมต้องมี : main cashier เขียนข้อมูลชุดนี้แบบยิงแล้วลืม ไม่มี .catch() เลย
    เคสจริง charter 2026-10-07 live บน Firestore ค้างอยู่ 3 ชม. ทั้งที่ jobchain ปกติ
    และ Firestore ของแท็บเดียวกันยังเขียนเลขใบเสร็จได้ แต่ไม่มีร่องรอยเหลือให้รู้ว่า
    ไม่ได้ยิง หรือยิงแล้วพังตรงไหน

    heartbeat stores/{storeId}/online/{computerId} (shop.ts) อ่านค่าพวกนี้ขึ้นไปด้วย
    support เปิด Firebase console ดูได้ทันทีโดยไม่ต้อง remote เข้าเครื่อง :
      liveSyncAttemptAt เดิน แต่ liveSyncOkAt ค้าง  -> ยิงแล้วแต่ Firestore ไม่ตอบ / พัง
      liveSyncAttemptAt ไม่เดินเลยทั้งที่เป็น main    -> โค้ด sync ไม่ได้ถูกเรียก
      liveSyncError                                 -> ขั้นที่พังล่าสุด + ข้อความ error
*/

// error เดิมซ้ำ ๆ ทุก 1.5 วิ (debounce ของ plugin) ส่ง Sentry แค่ครั้งเดียวต่อช่วงนี้
const SENTRY_THROTTLE_MS = 10 * 60 * 1000;
const MAX_ERROR_LENGTH = 300;

// ค่าทุกตัวต้องเป็น string หรือ null เท่านั้น RTDB set() จะ throw ถ้าเจอ undefined
const status = {
  liveSyncAttemptAt: null as string | null,
  liveSyncOkAt: null as string | null,
  liveSyncError: null as string | null,
  liveSyncErrorAt: null as string | null,
};

const lastSentryReport = new Map<string, number>();

export function getLiveSyncStatus() {
  return { ...status };
}

/** เครื่องนี้เป็น main และเริ่มรอบ sync แล้ว */
export function markLiveSyncAttempt() {
  status.liveSyncAttemptAt = dayjs().format();
}

/** Firestore ตอบรับ live แล้ว (doc ที่มือถืออ่าน) */
export function markLiveSyncOk() {
  status.liveSyncOkAt = dayjs().format();
}

export function reportLiveSyncError(step: string, error: any) {
  const message = String(error?.message || error).slice(0, MAX_ERROR_LENGTH);

  status.liveSyncError = `${step}: ${message}`;
  status.liveSyncErrorAt = dayjs().format();

  console.error(`[liveSync] ${step} ไม่สำเร็จ`, error);

  const key = `${step}:${message}`;
  const now = Date.now();
  if (now - (lastSentryReport.get(key) || 0) < SENTRY_THROTTLE_MS) return;
  lastSentryReport.set(key, now);

  Sentry.captureException(error instanceof Error ? error : new Error(message), {
    tags: { liveSyncStep: step },
  });
}

/**
 * รันการเขียน 1 ขั้น แยก error ของแต่ละขั้นออกจากกัน
 *
 * ต้องดักทั้ง throw ตรง ๆ และ promise reject เพราะ RTDB set() / Firestore setDoc()
 * ตรวจข้อมูลแบบ synchronous (เจอ NaN / undefined จะ throw ทันที ไม่ใช่ reject)
 * ของเดิมพอขั้นแรก throw ขั้นที่เหลือในฟังก์ชันเดียวกันก็ไม่ถูกเรียกเลย
 */
export function trackSyncWrite(step: string, write: () => any, onOk?: () => void) {
  try {
    const result = write();
    if (result && typeof result.then === "function") {
      result.then(
        () => onOk?.(),
        (error: any) => reportLiveSyncError(step, error)
      );
    } else {
      onOk?.();
    }
  } catch (error) {
    reportLiveSyncError(step, error);
  }
}
